import { useState, useEffect, useCallback } from "react";
import {
  Button, Space, Modal, Form, Select, DatePicker, InputNumber, Input, Tag, message,
} from "antd";
import {
  PlusOutlined, ReloadOutlined, PauseCircleOutlined, PlayCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import DataTable from "../../components/common/DataTable";
import ExportButton from "../../components/common/ExportButton";
import ConfirmDeleteModal from "../../components/common/ConfirmDeleteModal";
import usePermission from "../../hooks/usePermission";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import {
  FREQUENCY_OPTIONS, RECURRING_STATUS_OPTIONS, formatOmr,
} from "./constants";
import {
  listRecurringExpenses, createRecurringExpense, updateRecurringExpense,
  pauseRecurringExpense, resumeRecurringExpense, deleteRecurringExpense,
  exportRecurringExpenses,
} from "../../services/accounts.service";

const { TextArea } = Input;

/**
 * Recurring Expenses — templates the backend scheduler turns into company
 * expenses. "Next Generation" is when the scheduler will next fire.
 */
export default function RecurringExpensesTab() {
  const { canEdit, canDelete } = usePermission("accounts");
  const { options: billerOptions } = useSettingsDropdown("incentive-billers");
  const { options: categoryOptions } = useSettingsDropdown("biller-categories");
  const { options: bankOptions } = useSettingsDropdown("company-bank-accounts");
  const { options: paymentModeOptions } = useSettingsDropdown("payment-modes");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState();
  const [frequency, setFrequency] = useState();

  const [modal, setModal] = useState({ open: false, record: null });
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const params = useCallback(
    (extra = {}) => ({
      search: search || undefined,
      status: status || undefined,
      frequency: frequency || undefined,
      ...extra,
    }),
    [search, status, frequency]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listRecurringExpenses(params({ page, per_page: pageSize }));
      setRows(list?.data?.records || []);
      setTotalCount(list?.data?.pagination?.total_count || 0);
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not load recurring expenses");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [params, page, pageSize]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    setPage(1);
  }, [search, status, frequency]);

  const openModal = (record = null) => {
    setModal({ open: true, record });
    if (record) {
      form.setFieldsValue({
        expenseName: record.expenseName,
        biller: record.biller?._id,
        category: record.category?._id,
        startDate: record.startDate ? dayjs(record.startDate) : null,
        endDate: record.endDate ? dayjs(record.endDate) : null,
        expenseAmount: record.expenseAmount,
        vat: record.vat,
        frequency: record.frequency,
        sourceBankAccount: record.sourceBankAccount?._id,
        paymentMode: record.paymentMode?._id,
        description: record.description,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ startDate: dayjs(), frequency: "monthly", vat: 0 });
    }
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const payload = {
        ...values,
        startDate: values.startDate.format("YYYY-MM-DD"),
        endDate: values.endDate?.format("YYYY-MM-DD"),
      };
      if (modal.record) {
        await updateRecurringExpense(modal.record._id, payload);
        message.success("Recurring expense updated");
      } else {
        await createRecurringExpense(payload);
        message.success("Recurring expense created");
      }
      setModal({ open: false, record: null });
      fetchAll();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not save recurring expense");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (record) => {
    try {
      if (record.status === "active") {
        await pauseRecurringExpense(record._id);
        message.success("Paused — no further expenses will be generated");
      } else {
        await resumeRecurringExpense(record._id);
        message.success("Resumed from today (missed periods are not back-filled)");
      }
      fetchAll();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not change status");
    }
  };

  const handleDelete = (record) => {
    ConfirmDeleteModal({
      title: "Delete Recurring Expense",
      content: `Delete "${record.expenseName}"? Expenses already generated are kept.`,
      onConfirm: async () => {
        try {
          await deleteRecurringExpense(record._id);
          message.success("Recurring expense deleted");
          fetchAll();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Could not delete");
        }
      },
    });
  };

  const columns = [
    { title: "Expense Name", dataIndex: "expenseName" },
    { title: "Biller", dataIndex: "billerName" },
    { title: "Category", dataIndex: "categoryName" },
    { title: "Frequency", dataIndex: "frequencyLabel" },
    { title: "Amount (OMR)", dataIndex: "expenseAmount", align: "right", render: formatOmr },
    { title: "VAT", dataIndex: "vat", align: "right", render: formatOmr },
    { title: "Start", dataIndex: "startDateLabel" },
    { title: "End", dataIndex: "endDateLabel" },
    { title: "Next Generation", dataIndex: "nextGenerationDateLabel" },
    { title: "Generated", dataIndex: "generatedCount", align: "right" },
    {
      title: "Status",
      dataIndex: "status",
      render: (value) => (
        <Tag color={value === "active" ? "green" : "orange"}>
          {value === "active" ? "Active" : "Paused"}
        </Tag>
      ),
    },
    ...(canEdit
      ? [
          {
            title: "Action",
            key: "toggle",
            width: 120,
            render: (_, record) => (
              <Button
                size="small"
                icon={
                  record.status === "active" ? <PauseCircleOutlined /> : <PlayCircleOutlined />
                }
                onClick={() => toggle(record)}
              >
                {record.status === "active" ? "Pause" : "Resume"}
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search expense name"
          allowClear
          style={{ width: 240 }}
          onSearch={setSearch}
        />
        <Select
          placeholder="Status"
          allowClear
          style={{ width: 150 }}
          options={RECURRING_STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
        />
        <Select
          placeholder="Frequency"
          allowClear
          style={{ width: 150 }}
          options={FREQUENCY_OPTIONS}
          value={frequency}
          onChange={setFrequency}
        />
        <Button icon={<ReloadOutlined />} onClick={fetchAll}>
          Refresh
        </Button>
        <ExportButton
          moduleName="Recurring_Expenses"
          onExport={() => exportRecurringExpenses(params())}
        />
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
            Add Recurring Expense
          </Button>
        )}
      </Space>

      <DataTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ current: page, pageSize, total: totalCount }}
        onPageChange={setPage}
        actions={{
          onEdit: canEdit ? openModal : undefined,
          onDelete: canDelete ? handleDelete : undefined,
          canEdit,
          canDelete,
        }}
      />

      <Modal
        open={modal.open}
        title={modal.record ? "Edit Recurring Expense" : "Add Recurring Expense"}
        onCancel={() => setModal({ open: false, record: null })}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Save"
        width={620}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="expenseName" label="Expense Name" rules={[{ required: true }]}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="biller" label="Biller">
            <Select options={billerOptions} allowClear showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="category" label="Category">
            <Select options={categoryOptions} allowClear />
          </Form.Item>
          <Form.Item
            name="frequency"
            label="Frequency"
            rules={[{ required: true }]}
            extra="A company expense is generated automatically at each interval."
          >
            <Select options={FREQUENCY_OPTIONS} />
          </Form.Item>
          <Form.Item name="startDate" label="Start Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="endDate" label="End Date" extra="Leave blank to run until paused.">
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="expenseAmount" label="Expense Amount (OMR)" rules={[{ required: true }]}>
            <InputNumber style={{ width: "100%" }} precision={3} />
          </Form.Item>
          <Form.Item name="vat" label="VAT (OMR)">
            <InputNumber style={{ width: "100%" }} min={0} precision={3} />
          </Form.Item>
          <Form.Item name="sourceBankAccount" label="Source Bank Account">
            <Select options={bankOptions} allowClear />
          </Form.Item>
          <Form.Item name="paymentMode" label="Payment Mode">
            <Select options={paymentModeOptions} allowClear />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={3} maxLength={1000} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
